<script setup lang="ts">
import {
  ElButton,
  ElCard,
  ElDatePicker,
  ElIcon,
  ElSpace,
  ElSwitch,
  ElTable,
  ElTableColumn,
} from 'element-plus'
import { Location, Refresh, Search } from '@element-plus/icons-vue'
import { DateTime } from 'luxon'
import mapboxgl from 'mapbox-gl'
import 'mapbox-gl/dist/mapbox-gl.css'
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { GeoFencesApi, PingsApi, VisitsApi } from '~/services/apiClientGps'
import { parseWKTPolygon } from '~/utils/wkt'

// Initialize Mapbox
mapboxgl.accessToken =
  'pk.eyJ1Ijoib21pZHJvc2hhbmllIiwiYSI6ImNtOGxpaXkwNjBrMWkyaXF1dWtvYmh2b2kifQ.1V8b-WsQ6qkqveYjXgCvdg'

// Map style URLs
const MAP_STYLES = {
  streets: 'mapbox://styles/mapbox/streets-v12',
  satellite: 'mapbox://styles/mapbox/satellite-streets-v12',
}

const route = useRoute()
const router = useRouter()
const pingsApi = new PingsApi()
const geofencesApi = new GeoFencesApi()
const visitsApi = new VisitsApi()
const deviceId = computed(() => Number(route.params.id))

// Map refs
const mapContainer = ref<HTMLElement | null>(null)
const map = ref<mapboxgl.Map | null>(null)
const popup = ref<mapboxgl.Popup | null>(null)
const isSatelliteMode = ref(false)

// Date range
const dateRange = ref<[Date, Date]>([
  DateTime.now().minus({ hours: 1 }).toJSDate(),
  DateTime.now().toJSDate(),
])

// Data
const pings = ref<any[]>([])
const geofences = ref<any[]>([])
const loading = ref(false)

// Visits data
const visits = ref<any[]>([])
const loadingVisits = ref(false)

// Sort visits by arrival time
const sortedVisits = computed(() => {
  return [...visits.value].sort((a, b) => {
    return (
      DateTime.fromISO(b.arrival).toMillis() -
      DateTime.fromISO(a.arrival).toMillis()
    )
  })
})

// Show geofences on map
const showGeofencesOnMap = () => {
  if (!map.value || !geofences.value.length) return

  // Remove existing geofence layers
  const existingLayers = map.value.getStyle().layers || []
  existingLayers.forEach((layer) => {
    if (layer.id.startsWith('geofence-')) {
      map.value?.removeLayer(layer.id)
      map.value?.removeSource(layer.id)
    }
  })

  // Add each geofence
  geofences.value.forEach((fence, index) => {
    const coordinates = parseWKTPolygon(fence.polygon)
    if (!coordinates.length) {
      console.error('No valid coordinates found for geofence:', fence)
      return
    }

    const sourceId = `geofence-${index}`

    // Add geofence polygon
    map.value?.addSource(sourceId, {
      type: 'geojson',
      data: {
        type: 'Feature',
        properties: {
          name: fence.name,
        },
        geometry: {
          type: 'Polygon',
          coordinates,
        },
      },
    })

    // Add fill layer
    map.value?.addLayer({
      id: `${sourceId}-fill`,
      type: 'fill',
      source: sourceId,
      paint: {
        'fill-color': '#088',
        'fill-opacity': 0.2,
        'fill-outline-color': '#ff3b30',
      },
    })

    // Add label
    map.value?.addLayer({
      id: `${sourceId}-label`,
      type: 'symbol',
      source: sourceId,
      layout: {
        'text-field': ['get', 'name'],
        'text-size': 14,
        'text-justify': 'center',
        'text-anchor': 'center',
      },
      paint: {
        'text-color': '#1e293b',
        'text-halo-color': '#ffffff',
        'text-halo-width': 2,
      },
    })
  })
}

// Load geofences
const loadGeofences = async () => {
  try {
    const response = await geofencesApi.getAllGeoFencesGeoFencesGet()
    geofences.value = response.data
    showGeofencesOnMap()
  } catch (error) {
    console.error('Error loading geofences:', error)
  }
}

// Initialize popup
const initializePopup = () => {
  if (!map.value) return

  // Initialize popup if not exists
  if (!popup.value) {
    popup.value = new mapboxgl.Popup({
      closeButton: false,
      closeOnClick: false,
      className: 'route-popup',
    })
  }

  // Add hover effect
  map.value.on('mousemove', 'route-line', (e) => {
    if (!e.lngLat || !popup.value || !map.value) return

    // Find the closest ping to the hovered point
    const hoveredPoint = [e.lngLat.lng, e.lngLat.lat]
    let minDistance = Infinity
    let closestPing = pings.value[0]

    pings.value.forEach((ping) => {
      const distance = Math.sqrt(
        (ping.longitude - hoveredPoint[0]) ** 2 +
          (ping.latitude - hoveredPoint[1]) ** 2
      )
      if (distance < minDistance) {
        minDistance = distance
        closestPing = ping
      }
    })

    // Format the timestamp and speed with validation
    let formattedTime = 'Time not available'
    let formattedSpeed = 'Speed not available'

    if (closestPing?.time) {
      try {
        const dt = DateTime.fromISO(closestPing.time)
        if (dt.isValid) {
          formattedTime = dt.toLocaleString(DateTime.DATETIME_FULL)
        }
      } catch (error) {
        console.error('Error parsing time:', error)
      }
    }

    if (typeof closestPing?.recorded_speed_mph === 'number') {
      formattedSpeed = `${closestPing.recorded_speed_mph.toFixed(1)} mph`
    }

    // Show popup with time and speed
    const popupContent = `
      <div class="popup-content">
        <div class="popup-row">
          <span class="popup-label">Time:</span>
          <span class="popup-value">${formattedTime}</span>
        </div>
        <div class="popup-row">
          <span class="popup-label">Speed:</span>
          <span class="popup-value">${formattedSpeed}</span>
        </div>
      </div>
    `

    if (!map.value) return

    popup.value
      .setLngLat([closestPing.longitude, closestPing.latitude])
      .setHTML(popupContent)
      .addTo(map.value as unknown as mapboxgl.Map)
  })

  // Set cursor and remove popup on route hover
  map.value.on('mouseenter', 'route-line', () => {
    if (map.value) map.value.getCanvas().style.cursor = 'pointer'
  })

  map.value.on('mouseleave', 'route-line', () => {
    if (map.value) map.value.getCanvas().style.cursor = ''
    popup.value?.remove()
  })
}

// Show route on map
const showRouteOnMap = () => {
  if (!map.value || !pings.value.length) return

  // Remove existing route layers if they exist
  const existingLayers = map.value.getStyle().layers || []
  existingLayers.forEach((layer) => {
    if (layer.id.startsWith('route-')) {
      if (map.value?.getLayer(layer.id)) {
        map.value?.removeLayer(layer.id)
      }
    }
  })

  // Remove sources separately after layers
  const sourceIds = ['route-source', 'start-point', 'end-point']
  sourceIds.forEach((sourceId) => {
    if (map.value?.getSource(sourceId)) {
      map.value?.removeSource(sourceId)
    }
  })

  // Create coordinates array for the route
  const coordinates = pings.value.map((ping) => [ping.longitude, ping.latitude])

  // Add the route line
  map.value.addSource('route-source', {
    type: 'geojson',
    data: {
      type: 'Feature',
      properties: {},
      geometry: {
        type: 'LineString',
        coordinates,
      },
    },
  })

  map.value.addLayer({
    id: 'route-line',
    type: 'line',
    source: 'route-source',
    layout: {
      'line-join': 'round',
      'line-cap': 'round',
    },
    paint: {
      'line-color': '#2563eb',
      'line-width': 3,
    },
  })

  // Add start point
  map.value.addSource('start-point', {
    type: 'geojson',
    data: {
      type: 'Feature',
      properties: {},
      geometry: {
        type: 'Point',
        coordinates: coordinates[0],
      },
    },
  })

  map.value.addLayer({
    id: 'start-point',
    type: 'circle',
    source: 'start-point',
    paint: {
      'circle-radius': 8,
      'circle-color': '#22c55e',
      'circle-stroke-width': 2,
      'circle-stroke-color': '#ffffff',
    },
  })

  // Add end point
  map.value.addSource('end-point', {
    type: 'geojson',
    data: {
      type: 'Feature',
      properties: {},
      geometry: {
        type: 'Point',
        coordinates: coordinates[coordinates.length - 1],
      },
    },
  })

  map.value.addLayer({
    id: 'end-point',
    type: 'circle',
    source: 'end-point',
    paint: {
      'circle-radius': 8,
      'circle-color': '#ef4444',
      'circle-stroke-width': 2,
      'circle-stroke-color': '#ffffff',
    },
  })

  // Calculate bounds and center
  const bounds = new mapboxgl.LngLatBounds()
  coordinates.forEach((coord) => bounds.extend(coord as [number, number]))

  // Add padding and fit bounds with animation
  map.value.fitBounds(bounds, {
    padding: {
      top: 50,
      bottom: 50,
      left: 50,
      right: 50,
    },
    duration: 1000,
    maxZoom: 16,
  })

  // Initialize popup and hover events
  initializePopup()
}

// Toggle satellite mode
const toggleSatelliteMode = () => {
  const mapInstance = map.value
  if (!mapInstance) return

  const style = isSatelliteMode.value
    ? MAP_STYLES.satellite
    : MAP_STYLES.streets

  // Store current data
  const currentPings = [...pings.value]
  const currentGeofences = [...geofences.value]

  // Create a new map instance with the new style
  mapInstance.setStyle(style)

  // Wait for the style to load
  mapInstance.once('style.load', () => {
    if (!mapInstance) return

    // Add navigation controls back
    if (!mapInstance.hasControl(new mapboxgl.NavigationControl())) {
      mapInstance.addControl(new mapboxgl.NavigationControl(), 'top-right')
    }

    // Restore data
    pings.value = currentPings
    geofences.value = currentGeofences

    // Add layers back in correct order
    showGeofencesOnMap()
    if (pings.value.length > 0) {
      showRouteOnMap()
    }
  })
}

// Load pings
const loadPings = async () => {
  if (!deviceId.value || !dateRange.value[0] || !dateRange.value[1]) return

  try {
    loading.value = true

    // Safely remove existing route layers and sources if they exist
    if (map.value) {
      const layerIds = ['route-line', 'start-point', 'end-point']
      layerIds.forEach((id) => {
        if (map.value?.getLayer(id)) {
          map.value.removeLayer(id)
        }
      })

      const sourceIds = ['route-source', 'start-point', 'end-point']
      sourceIds.forEach((id) => {
        if (map.value?.getSource(id)) {
          map.value.removeSource(id)
        }
      })
    }

    const response = await pingsApi.getPingsByDevicePingsByDeviceDeviceIdGet(
      deviceId.value,
      DateTime.fromJSDate(dateRange.value[0]).toISO(),
      DateTime.fromJSDate(dateRange.value[1]).toISO()
    )
    pings.value = response.data
    showRouteOnMap()
  } catch (error) {
    console.error('Error loading pings:', error)
  } finally {
    loading.value = false
  }
}

// Handle date range change
const handleDateRangeChange = (dates: [Date, Date]) => {
  dateRange.value = dates
  loadPings()
}

// Go back to devices list
const goBack = () => {
  router.push('/devices')
}

// Load visits
const loadVisits = async () => {
  if (!deviceId.value) return

  try {
    loadingVisits.value = true
    const response = await visitsApi.getVisitsVisitsGet(
      deviceId.value.toString()
    )
    visits.value = response.data.items
  } catch (error: any) {
    // Handle 404 gracefully
    if (error?.response?.status === 404) {
      visits.value = []
    } else {
      console.error('Error loading visits:', error)
    }
  } finally {
    loadingVisits.value = false
  }
}

// Format duration between two timestamps
const calculateDuration = (start: string, end: string) => {
  const startTime = DateTime.fromISO(start)
  const endTime = DateTime.fromISO(end)
  const diff = endTime.diff(startTime, ['hours', 'minutes'])

  const hours = Math.floor(diff.hours)
  const minutes = Math.floor(diff.minutes)

  if (hours === 0) {
    return `${minutes}m`
  }
  return `${hours}h ${minutes}m`
}

// Format timestamp
const formatTimestamp = (timestamp: string) => {
  return DateTime.fromISO(timestamp).toLocaleString(DateTime.DATETIME_SHORT)
}

// Set date range and reload pings for a specific visit
const showVisitRoute = (visit: any) => {
  dateRange.value = [
    DateTime.fromISO(visit.arrival).toJSDate(),
    DateTime.fromISO(visit.departure).toJSDate(),
  ]
  loadPings()
}

// Load initial data
onMounted(() => {
  if (mapContainer.value) {
    const mapOptions: mapboxgl.MapboxOptions = {
      container: mapContainer.value,
      style: MAP_STYLES.streets,
      center: [-118.2437, 33.7271],
      zoom: 8,
      maxZoom: 16,
    }

    const mapInstance = new mapboxgl.Map(mapOptions)
    map.value = mapInstance

    // Add navigation controls
    mapInstance.addControl(new mapboxgl.NavigationControl(), 'top-right')

    // Initial data load after style is ready
    const initialLoadHandler = () => {
      loadPings()
      loadGeofences()
      loadVisits()
    }
    mapInstance.on('style.load', initialLoadHandler)
  }
})

// Add styles for the popup
const style = document.createElement('style')
style.textContent = `
.route-popup {
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

.route-popup .mapboxgl-popup-content {
  padding: 16px;
  border-radius: 12px;
  border: none;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(8px);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.12), 
              0 1px 3px rgba(0, 0, 0, 0.08);
  transform-origin: 50% 0%;
  animation: popup-appear 0.2s ease-out;
}

.route-popup .mapboxgl-popup-tip {
  display: none;
}

@keyframes popup-appear {
  from {
    opacity: 0;
    transform: translateY(-4px) scale(0.98);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.popup-content {
  min-width: 200px;
}

.popup-row {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  padding: 4px 0;
}

.popup-row:not(:last-child) {
  border-bottom: 1px solid rgba(100, 116, 139, 0.1);
  margin-bottom: 4px;
}

.popup-label {
  color: #64748b;
  font-size: 0.875rem;
  font-weight: 500;
}

.popup-value {
  color: #0f172a;
  font-size: 0.875rem;
  font-weight: 600;
  text-align: right;
}
`
document.head.appendChild(style)

onUnmounted(() => {
  if (map.value) {
    map.value.remove()
  }
})
</script>

<template>
  <div class="route-container">
    <!-- Map Card -->
    <ElCard class="map-card">
      <template #header>
        <div class="card-header">
          <div class="header-left">
            <ElButton @click="goBack"> Back to Devices </ElButton>
            <ElIcon class="header-icon"><Location /></ElIcon>
            <h2>Device Route</h2>
          </div>
          <div class="controls">
            <ElSpace>
              <ElDatePicker
                v-model="dateRange"
                type="datetimerange"
                range-separator="to"
                start-placeholder="Start Time"
                end-placeholder="End Time"
                :default-time="[
                  new Date(2000, 1, 1, 0, 0, 0),
                  new Date(2000, 1, 1, 23, 59, 59),
                ]"
                @change="handleDateRangeChange"
              />
              <ElButton
                type="primary"
                :icon="Refresh"
                :loading="loading"
                @click="loadPings"
              >
                Refresh
              </ElButton>
              <div class="map-mode">
                <ElSwitch
                  v-model="isSatelliteMode"
                  active-text="Satellite"
                  inactive-text="Streets"
                  @change="toggleSatelliteMode"
                />
              </div>
            </ElSpace>
          </div>
        </div>
      </template>

      <div ref="mapContainer" class="map-container"></div>

      <div v-if="pings.length" class="route-info">
        <div class="info-item">
          <span class="label">Total Points:</span>
          <span class="value">{{ pings.length }}</span>
        </div>
        <div class="info-item">
          <span class="label">Start Time:</span>
          <span class="value">{{
            new Date(pings[0].time).toLocaleString()
          }}</span>
        </div>
        <div class="info-item">
          <span class="label">End Time:</span>
          <span class="value">{{
            new Date(pings[pings.length - 1].time).toLocaleString()
          }}</span>
        </div>
      </div>
    </ElCard>

    <!-- Visits Card -->
    <ElCard class="visits-card">
      <template #header>
        <div class="card-header">
          <h3 class="visits-title">Geofence Visits</h3>
          <ElButton
            type="primary"
            :icon="Refresh"
            :loading="loadingVisits"
            @click="loadVisits"
          >
            Refresh Visits
          </ElButton>
        </div>
      </template>

      <ElTable
        v-loading="loadingVisits"
        :data="sortedVisits"
        style="width: 100%"
        stripe
        border
        :default-sort="{ prop: 'arrival', order: 'descending' }"
      >
        <template #empty>
          <div class="empty-state">
            <p>No visits found for this device</p>
          </div>
        </template>

        <ElTableColumn label="Actions" width="100" fixed="right">
          <template #default="{ row }">
            <ElButton
              type="primary"
              :icon="Search"
              circle
              title="Show route during this visit"
              @click="showVisitRoute(row)"
            />
          </template>
        </ElTableColumn>
        <ElTableColumn label="Geofence" min-width="150" sortable>
          <template #default="{ row }">
            {{ row.geo_fence.name }}
          </template>
        </ElTableColumn>
        <ElTableColumn label="Arrival" min-width="160" sortable prop="arrival">
          <template #default="{ row }">
            {{ formatTimestamp(row.arrival) }}
          </template>
        </ElTableColumn>
        <ElTableColumn
          label="Departure"
          min-width="160"
          sortable
          prop="departure"
        >
          <template #default="{ row }">
            {{ formatTimestamp(row.departure) }}
          </template>
        </ElTableColumn>
        <ElTableColumn label="Duration" min-width="100" sortable>
          <template #default="{ row }">
            {{ calculateDuration(row.arrival, row.departure) }}
          </template>
        </ElTableColumn>
        <ElTableColumn
          label="Last Ping"
          min-width="160"
          sortable
          prop="last_ping"
        >
          <template #default="{ row }">
            {{ formatTimestamp(row.last_ping) }}
          </template>
        </ElTableColumn>
      </ElTable>
    </ElCard>
  </div>
</template>

<style scoped>
.route-container {
  padding: 20px;
  min-height: calc(100vh - 100px);
  background-color: #f5f7fa;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.map-card,
.visits-card {
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.1);
}

.map-card {
  flex: 1;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 0;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.header-icon {
  font-size: 24px;
  color: var(--el-color-primary);
}

.header-left h2 {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: #2c3e50;
}

.map-container {
  height: calc(100vh - 300px);
  border-radius: 8px;
  overflow: hidden;
  margin: 16px 0;
}

.route-info {
  display: flex;
  gap: 24px;
  padding: 16px;
  background-color: #f8fafc;
  border-radius: 8px;
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.info-item .label {
  font-size: 12px;
  color: #64748b;
  font-weight: 500;
}

.info-item .value {
  font-size: 14px;
  color: #1e293b;
  font-weight: 600;
}

.map-mode {
  display: flex;
  align-items: center;
  margin-left: 8px;
}

.visits-title {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
}

:deep(.el-table) {
  border-radius: 8px;
  overflow: hidden;
}

:deep(.el-table th) {
  background-color: #f8fafc;
  color: #64748b;
  font-weight: 600;
}

:deep(.el-table td) {
  color: #1e293b;
}

:deep(.el-table--striped .el-table__body tr.el-table__row--striped td) {
  background-color: #f8fafc;
}

:deep(.el-switch__label) {
  color: #64748b;
  font-size: 12px;
}

:deep(.el-switch__label.is-active) {
  color: #1e293b;
}

:deep(.el-button.is-circle) {
  padding: 8px;
}

:deep(.el-table .cell) {
  display: flex;
  justify-content: center;
}

.empty-state {
  padding: 32px;
  text-align: center;
  color: #64748b;
}

.empty-state p {
  margin: 0;
  font-size: 14px;
}
</style>
